import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Calendar, Heart, MapPin, QrCode, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

type TicketRow = {
  code: string
  checked_in_at: string | null
}

type LineItemRow = {
  id: string
  tier_name: string
  quantity: number
  unit_amount_cents: number
  tickets: TicketRow[]
}

type EventRow = {
  slug: string
  title: string
  date: string | null
  venue_name: string | null
  city: string | null
  state: string | null
}

type OrganizationRow = {
  slug: string
  name: string
  logo_url: string | null
}

type PurchaseRow = {
  id: string
  amount_cents: number
  total_quantity: number
  paid_at: string
  kind: 'ticket' | 'sponsorship' | 'ad' | 'donation'
  checked_in_count: number
  total_tickets: number
  event: EventRow
  organization: OrganizationRow
  line_items: LineItemRow[]
}

type DonationRow = {
  id: string
  amount_cents: number
  paid_at: string
  event: EventRow
  organization: OrganizationRow
}

type RecurringDonationRow = {
  id: string
  amount_cents: number
  status: 'pending' | 'active' | 'past_due' | 'canceled'
  current_period_end: string | null
  created_at: string
  event: EventRow
  organization: OrganizationRow
}

type Props = {
  upcoming_tickets: PurchaseRow[]
  past_tickets: PurchaseRow[]
  donations: DonationRow[]
  recurring_donations: RecurringDonationRow[]
}

function formatDollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
}

function formatDate(dateStr: string | null): string | null {
  if (!dateStr) return null
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function EventMeta({ event }: { event: EventRow }) {
  const dateLabel = formatDate(event.date)
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
      {dateLabel && (
        <span className="flex items-center gap-1.5">
          <Calendar className="size-4" />
          {dateLabel}
        </span>
      )}
      {(event.venue_name || event.city) && (
        <span className="flex items-center gap-1.5">
          <MapPin className="size-4" />
          {[event.venue_name, event.city, event.state].filter(Boolean).join(', ')}
        </span>
      )}
    </div>
  )
}

function EmptyState({ icon: Icon, title, body, ctaHref, ctaLabel }: {
  icon: typeof QrCode
  title: string
  body: string
  ctaHref: string
  ctaLabel: string
}) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Icon className="mx-auto size-12 text-muted-foreground" />
        <h2 className="mt-4 text-xl font-semibold">{title}</h2>
        <p className="mt-2 text-muted-foreground">{body}</p>
        <Link
          href={ctaHref}
          className="mt-4 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {ctaLabel}
        </Link>
      </CardContent>
    </Card>
  )
}

function PurchaseHeader({ purchase, statusBadge }: { purchase: PurchaseRow; statusBadge?: React.ReactNode }) {
  return (
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle className="text-xl">
            <Link href={`/events/${purchase.event.slug}`} className="hover:underline">
              {purchase.event.title}
            </Link>
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            <Link href={`/o/${purchase.organization.slug}`} className="hover:underline">
              {purchase.organization.name}
            </Link>
          </p>
        </div>
        {statusBadge ?? (
          <Badge variant="outline" className="capitalize">{purchase.kind}</Badge>
        )}
      </div>
    </CardHeader>
  )
}

function TicketCodes({ lineItems }: { lineItems: LineItemRow[] }) {
  return (
    <>
      {lineItems.map(line => (
        <div key={line.id} className="space-y-2">
          <p className="text-sm font-medium">
            {line.tier_name} <span className="text-muted-foreground">× {line.quantity}</span>
          </p>
          {line.tickets.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {line.tickets.map(ticket => (
                <code
                  key={ticket.code}
                  className="rounded-md border border-black/10 bg-card px-2.5 py-1 font-mono text-sm"
                  title={ticket.checked_in_at ? `Checked in ${ticket.checked_in_at}` : 'Not yet checked in'}
                >
                  {ticket.code}
                  {ticket.checked_in_at && <span className="ml-1.5 text-xs text-green-600">✓</span>}
                </code>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  )
}

function UpcomingCard({ purchase }: { purchase: PurchaseRow }) {
  return (
    <Card>
      <PurchaseHeader purchase={purchase} />
      <CardContent className="space-y-3">
        <EventMeta event={purchase.event} />
        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
          <span>Quantity: <span className="font-medium">{purchase.total_quantity}</span></span>
          <span>Total paid: <span className="font-medium">{formatDollars(purchase.amount_cents)}</span></span>
        </div>
        <TicketCodes lineItems={purchase.line_items} />
      </CardContent>
    </Card>
  )
}

function PastCard({ purchase }: { purchase: PurchaseRow }) {
  const [showCodes, setShowCodes] = useState(false)
  const fullyCheckedIn = purchase.total_tickets > 0 && purchase.checked_in_count === purchase.total_tickets
  const partiallyCheckedIn = purchase.checked_in_count > 0 && !fullyCheckedIn

  const statusBadge = purchase.total_tickets > 0 ? (
    <Badge
      variant="outline"
      className={
        fullyCheckedIn
          ? 'border-green-600/30 bg-green-50 text-green-700'
          : partiallyCheckedIn
            ? 'border-amber-600/30 bg-amber-50 text-amber-700'
            : 'border-muted-foreground/30 bg-muted text-muted-foreground'
      }
    >
      Checked in: {purchase.checked_in_count} / {purchase.total_tickets}
    </Badge>
  ) : (
    <Badge variant="outline" className="capitalize">{purchase.kind}</Badge>
  )

  return (
    <Card>
      <PurchaseHeader purchase={purchase} statusBadge={statusBadge} />
      <CardContent className="space-y-3">
        <EventMeta event={purchase.event} />
        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
          <span>Quantity: <span className="font-medium">{purchase.total_quantity}</span></span>
          <span>Total paid: <span className="font-medium">{formatDollars(purchase.amount_cents)}</span></span>
        </div>
        {purchase.line_items.length > 0 && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCodes(v => !v)}
              className="h-auto px-0 text-sm font-medium text-primary hover:bg-transparent hover:underline"
            >
              {showCodes ? 'Hide ticket codes' : 'Show ticket codes'}
            </Button>
            {showCodes && <TicketCodes lineItems={purchase.line_items} />}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function DonationCard({ donation }: { donation: DonationRow }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl">
              <Link href={`/events/${donation.event.slug}`} className="hover:underline">
                {donation.event.title}
              </Link>
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              <Link href={`/o/${donation.organization.slug}`} className="hover:underline">
                {donation.organization.name}
              </Link>
            </p>
          </div>
          <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary">
            <Heart className="size-3" />
            Donation
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <EventMeta event={donation.event} />
        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
          <span>Donated <span className="font-medium">{formatTimestamp(donation.paid_at)}</span></span>
          <span>Amount: <span className="font-medium">{formatDollars(donation.amount_cents)}</span></span>
        </div>
      </CardContent>
    </Card>
  )
}

function RecurringCard({ recurring }: { recurring: RecurringDonationRow }) {
  const [canceling, setCanceling] = useState(false)

  function handleCancel() {
    if (!window.confirm('Cancel this monthly donation? No further charges will be made.')) return
    router.delete(`/recurring-donations/${recurring.id}/cancel`, {
      preserveScroll: true,
      onStart: () => setCanceling(true),
      onFinish: () => setCanceling(false),
    })
  }

  const nextCharge = recurring.current_period_end
    ? formatTimestamp(recurring.current_period_end)
    : null

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl">
              <Link href={`/events/${recurring.event.slug}`} className="hover:underline">
                {recurring.event.title}
              </Link>
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              <Link href={`/o/${recurring.organization.slug}`} className="hover:underline">
                {recurring.organization.name}
              </Link>
            </p>
          </div>
          <Badge
            variant="outline"
            className={
              recurring.status === 'past_due'
                ? 'border-amber-600/30 bg-amber-50 text-amber-700'
                : 'border-primary/30 bg-primary/5 text-primary'
            }
          >
            <RefreshCw className="size-3" />
            {recurring.status === 'past_due' ? 'Past due' : 'Monthly'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <EventMeta event={recurring.event} />
        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
          <span>
            <span className="font-medium">{formatDollars(recurring.amount_cents)}</span> / month
          </span>
          {nextCharge && (
            <span className="text-muted-foreground">Next charge {nextCharge}</span>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={canceling}
          >
            {canceling ? 'Canceling…' : 'Cancel monthly donation'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default function MyTickets({ upcoming_tickets, past_tickets, donations, recurring_donations }: Props) {
  return (
    <>
      <Head title="My Tickets" />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold">My events</h1>
          <p className="mt-1 text-muted-foreground">
            Every ticket you've claimed and every donation you've made on Gala Season.
          </p>
        </div>

        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming ({upcoming_tickets.length})</TabsTrigger>
            <TabsTrigger value="past">Past ({past_tickets.length})</TabsTrigger>
            <TabsTrigger value="donations">Donations ({donations.length})</TabsTrigger>
            {recurring_donations.length > 0 && (
              <TabsTrigger value="recurring">Monthly ({recurring_donations.length})</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="upcoming">
            {upcoming_tickets.length === 0 ? (
              <EmptyState
                icon={QrCode}
                title="No upcoming events"
                body="Tickets you claim for upcoming events will show up here."
                ctaHref="/events"
                ctaLabel="Browse events"
              />
            ) : (
              <div className="space-y-4">
                {upcoming_tickets.map(p => <UpcomingCard key={p.id} purchase={p} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {past_tickets.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No past events yet"
                body="Once you attend an event, it'll appear here with your check-in status."
                ctaHref="/past-events"
                ctaLabel="Browse past events"
              />
            ) : (
              <div className="space-y-4">
                {past_tickets.map(p => <PastCard key={p.id} purchase={p} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="donations">
            {donations.length === 0 ? (
              <EmptyState
                icon={Heart}
                title="No donations yet"
                body="Donations you make to events and fundraisers will show up here."
                ctaHref="/events"
                ctaLabel="Browse events"
              />
            ) : (
              <div className="space-y-4">
                {donations.map(d => <DonationCard key={d.id} donation={d} />)}
              </div>
            )}
          </TabsContent>

          {recurring_donations.length > 0 && (
            <TabsContent value="recurring">
              <div className="space-y-4">
                {recurring_donations.map(r => <RecurringCard key={r.id} recurring={r} />)}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </>
  )
}
